import { useEffect, useRef, useState } from "react";
import { IMAGE_ACCEPT } from "@/lib/uploadAccept";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon, Plus, Trash2 } from "lucide-react";
import { sampleIdentity } from "@hisense/shared";
import { describe, isFallback, withDescription, type DescriptionLang } from "@hisense/shared";
import { SampleFileButton } from "./SampleFileButton";
import type { ProductSummaryDto, SampleItemDto, SampleItemInput } from "@hisense/shared";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { getLocalizedError, uploadFile } from "@/lib/api";
import { parseIntField } from "@/lib/moneyInput";
import { LabelPrintButton } from "@/components/domain/LabelPrintButton";
import { FieldGrid, FormGroup, SPAN_CLASS } from "@/components/ui/FormLayout";
import { cn } from "@/lib/cn";
import { SAMPLE_LINE_ROWS, type SampleLineFieldKey } from "./sampleFormLayout";

/**
 * The sample-order line repeater, extracted from SampleOrderFormPanel so the
 * editor page stays readable. Same module contract as the quotation/PI line
 * editor: a draft interface, a `blank*` factory, `*FromProduct` / `*FromDto`
 * hydrators, and a pure drafts→input converter that returns a discriminated
 * union so the page can do `if ("error" in conv)`.
 *
 * A sample order carries NO money — quantities are plain integers, parsed with
 * parseIntField (reject junk, never coerce to 0).
 */
export interface SampleLineDraft {
  key: string;
  productId: string;
  productNo: string;
  subRef: string;
  type: string;
  description: string;
  /** Bản tiếng Hoa, chụp cùng lúc với bản tiếng Anh. */
  descriptionZh: string;
  brand: string;
  color: string;
  size: string;
  requestQuantity: string;
  retainedQuantity: string;
  remarks: string;
  isManualEntry: boolean;
  /** Manual lines upload their own image; catalog lines show the product's. */
  imageUrl: string;
  imageKey: string;
  controlSampleUrl: string;
  controlSampleKey: string;
  controlSampleDescription: string;
}

export function blankSampleLine(manual: boolean): SampleLineDraft {
  return {
    key: crypto.randomUUID(), productId: "", productNo: "", subRef: "", type: "", description: "", descriptionZh: "",
    brand: "", color: "", size: "", requestQuantity: "", retainedQuantity: "", remarks: "",
    isManualEntry: manual, imageUrl: "", imageKey: "",
    controlSampleUrl: "", controlSampleKey: "", controlSampleDescription: "",
  };
}

/**
 * A catalog line borrows the product's image for display only — it deliberately
 * does NOT own an imageKey, and sampleDraftsToInput drops both fields for it so
 * the server resolves the image through the product.
 */
export function sampleLineFromProduct(p: ProductSummaryDto): SampleLineDraft {
  return {
    ...blankSampleLine(false),
    productId: p.id, productNo: p.productCode, subRef: p.subRef ?? "", type: p.type ?? "",
    description: p.description ?? "", descriptionZh: p.descriptionZh ?? "", brand: p.brandName ?? "", color: p.color ?? "", size: p.size ?? "",
    imageUrl: p.imageUrl ?? "",
  };
}

/** Hydrate edit mode. Every DTO field maps back to its string draft; 0 stays "0". */
export function sampleLineFromDto(it: SampleItemDto): SampleLineDraft {
  return {
    key: crypto.randomUUID(),
    productId: it.productId ?? "",
    productNo: it.productNo ?? "",
    subRef: it.subRef ?? "",
    type: it.type ?? "",
    description: it.description ?? "",
    descriptionZh: it.descriptionZh ?? "",
    brand: it.brand ?? "",
    color: it.color ?? "",
    size: it.size ?? "",
    requestQuantity: String(it.requestQuantity),
    retainedQuantity: String(it.retainedQuantity),
    remarks: it.remarks ?? "",
    isManualEntry: Boolean(it.isManualEntry),
    imageUrl: it.imageUrl ?? "",
    imageKey: it.imageKey ?? "",
    controlSampleUrl: it.controlSampleUrl ?? "",
    controlSampleKey: it.controlSampleKey ?? "",
    controlSampleDescription: it.controlSampleDescription ?? "",
  };
}

/** True when nothing identifies the line — those rows are dropped, not saved. */
const isEmptyLine = (l: SampleLineDraft) =>
  !l.productId && !l.productNo.trim() && !l.description.trim();

/**
 * Drafts → API line inputs. `field` is a FULL i18n key so the page can render
 * `t("common.lineError", { line, field: t(conv.error.field) })`.
 */
export function sampleDraftsToInput(lines: SampleLineDraft[]):
  | { items: SampleItemInput[] }
  | { error: { line: number; field: string } } {
  const items: SampleItemInput[] = [];
  for (const [i, it] of lines.entries()) {
    const req = parseIntField(it.requestQuantity);
    const ret = parseIntField(it.retainedQuantity);
    if (!isEmptyLine(it) && (req.invalid || req.value === undefined)) return { error: { line: i + 1, field: "samples.requestQuantity" } };
    if (ret.invalid) return { error: { line: i + 1, field: "samples.retainedQuantity" } };
    if (isEmptyLine(it)) continue;
    items.push({
      productId: it.productId || undefined,
      productNo: it.productNo.trim() || undefined,
      subRef: it.subRef.trim() || undefined,
      type: it.type || undefined,
      description: it.description.trim() || undefined,
      descriptionZh: it.descriptionZh.trim() || undefined,
      brand: it.brand.trim() || undefined,
      color: it.color.trim() || undefined,
      size: it.size.trim() || undefined,
      requestQuantity: req.value ?? 0,
      retainedQuantity: ret.value ?? 0,
      remarks: it.remarks.trim() || undefined,
      isManualEntry: it.isManualEntry,
      // Only a manual line owns its image; a catalog line resolves through the product.
      imageUrl: it.isManualEntry ? it.imageUrl || undefined : undefined,
      imageKey: it.isManualEntry ? it.imageKey || undefined : undefined,
      controlSampleUrl: it.controlSampleUrl || undefined,
      controlSampleKey: it.controlSampleKey || undefined,
      controlSampleDescription: it.controlSampleDescription.trim() || undefined,
    });
  }
  return { items };
}

/**
 * Legacy per-line rules for a MANUAL line (SampleOrder.js:1380-1440): product
 * code, type and description are required, and a product code may not repeat.
 * Pure, so the page can call it before sampleDraftsToInput; `messageKey` is a
 * complete sentence — render it with `setError(t(v.messageKey))` (the line
 * number is available on `v.line` for a prefix if the page wants one).
 */
export function validateSampleLines(lines: SampleLineDraft[]): { line: number; messageKey: string } | null {
  const seen = new Map<string, number>();
  for (const [i, l] of lines.entries()) {
    if (isEmptyLine(l)) continue;
    if (l.isManualEntry) {
      if (!l.productNo.trim()) return { line: i + 1, messageKey: "samples.productNoRequired" };
      if (!l.type.trim()) return { line: i + 1, messageKey: "samples.typeRequired" };
      if (!l.description.trim()) return { line: i + 1, messageKey: "samples.descriptionRequired" };
    }
    const code = sampleIdentity(l.productNo,l.subRef);
    if (!code) continue;
    if (seen.has(code)) return { line: i + 1, messageKey: "samples.duplicateProductNo" };
    seen.set(code, i);
  }
  return null;
}

const selectClass = "h-9 w-full";
export function SampleOrderLinesEditor({
  lines,
  onChange,
  productTypes,
  onAddProduct,
  onUploadStateChange,
  readOnly = false,
  lang = "en",
}: {
  /** Ngôn ngữ in của chính đơn mẫu này. */
  lang?: DescriptionLang;
  lines: SampleLineDraft[];
  onChange: (next: SampleLineDraft[] | ((previous: SampleLineDraft[]) => SampleLineDraft[])) => void;
  readOnly?: boolean;
  /** settings.businessOperations.optionLists.productTypes */
  productTypes: string[];
  /** The parent owns the ProductPickerModal; this only raises the intent. */
  onAddProduct: () => void;
  /** Lets the page block submit / warn on navigate while an upload is in flight. */
  onUploadStateChange?: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<string | null>(null);
  const [collapsed,setCollapsed] = useState<Set<string>>(new Set());
  const uploadLock = useRef(false);
  const [err, setErr] = useState<string | null>(null);

  // Kept in a ref so an inline parent callback does not re-fire the effect on
  // every parent render — only a real busy→idle transition notifies.
  const notify = useRef(onUploadStateChange);
  notify.current = onUploadStateChange;
  useEffect(() => {
    notify.current?.(busy !== null);
  }, [busy]);

  const patch = (key: string, next: Partial<SampleLineDraft>) =>
    onChange(previous => previous.map((it) => (it.key === key ? { ...it, ...next, ...(it.isManualEntry && (next.productNo !== undefined || next.subRef !== undefined) ? {productId:""} : {}) } : it)));
  const remove = (key: string) => onChange(previous => previous.filter((it) => it.key !== key));

  const onLineImage = async (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
    field: "image" | "control",
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || readOnly || uploadLock.current) return;
    uploadLock.current = true;
    setBusy(`${key}:${field}`);
    setErr(null);
    try {
      const f = await uploadFile(file, "sample-orders");
      patch(key, field === "image"
        ? { imageUrl: f.url, imageKey: f.key }
        : { controlSampleUrl: f.url, controlSampleKey: f.key });
    } catch (e2) {
      setErr(getLocalizedError(e2, t, "samples.errorUpload"));
    } finally {
      uploadLock.current = false;
      setBusy(null);
    }
  };

  /**
   * Ô nhập của TỪNG trường một dòng mẫu, xếp theo SAMPLE_LINE_ROWS (sampleFormLayout.ts, 19/09/2026). `Record` bắt buộc đủ ô —
   * thiếu là lỗi biên dịch. Logic giữ nguyên bản trước: dòng danh mục khoá mã / mã phụ, chỉ dòng gõ tay tự tải ảnh riêng.
   */
  const cells: Record<SampleLineFieldKey, (it: SampleLineDraft, i: number) => React.ReactNode> = {
    image: (it, i) => (
      <div>
        <FieldLabel>{t("products.image")}</FieldLabel>
        <div className="flex flex-col items-start gap-1.5">
          <span className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-sm border border-line bg-surface">
            {it.imageUrl
              ? <img src={it.imageUrl} alt={t("products.image")} className="size-full object-cover" />
              : <ImageIcon className="size-5 text-ink-faint" />}
          </span>
          {it.isManualEntry ? (
            <>
              <SampleFileButton accept={IMAGE_ACCEPT} disabled={readOnly || busy !== null}
                label={busy === `${it.key}:image` ? t("common.uploading") : `${t("products.uploadImage")} ${i+1}`}
                onChange={e=>void onLineImage(e,it.key,"image")}/>
              {it.imageUrl ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => patch(it.key, { imageUrl: "", imageKey: "" })}>
                  {t("products.removeImage")}
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    ),
    productNo: (it) => (
      <div>
        <FieldLabel htmlFor={`${it.key}-productNo`}>{t("samples.productNo")}</FieldLabel>
        <Input id={`${it.key}-productNo`} value={it.productNo} readOnly={!it.isManualEntry} className="font-mono tabular"
          onChange={(e) => patch(it.key, { productNo: e.target.value })} />
      </div>
    ),
    subRef: (it) => (
      <div>
        <FieldLabel htmlFor={`${it.key}-subRef`}>{t("samples.subRef")}</FieldLabel>
        <Input id={`${it.key}-subRef`} value={it.subRef} readOnly={!it.isManualEntry} className="font-mono tabular" onChange={(e) => patch(it.key, { subRef: e.target.value })} />
      </div>
    ),
    type: (it) => (
      <div>
        <FieldLabel htmlFor={`${it.key}-type`}>{t("common.type")}</FieldLabel>
        <Select id={`${it.key}-type`} containerClassName={selectClass} value={it.type} onChange={(e) => patch(it.key, { type: e.target.value })}>
          <option value="">{t("products.selectType")}</option>
          {productTypes.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          {it.type && !productTypes.includes(it.type) ? <option value={it.type}>{it.type}</option> : null}
        </Select>
      </div>
    ),
    brand: (it) => (
      <div>
        <FieldLabel htmlFor={`${it.key}-brand`}>{t("products.brand")}</FieldLabel>
        <Input id={`${it.key}-brand`} value={it.brand} onChange={(e) => patch(it.key, { brand: e.target.value })} />
      </div>
    ),
    description: (it) => (
      <div>
        <FieldLabel htmlFor={`${it.key}-description`}>{t("common.description")} · {t(`common.printLanguage_${lang}`)}</FieldLabel>
        <Input id={`${it.key}-description`} value={lang === "zh" ? it.descriptionZh : it.description}
          title={(lang === "zh" ? it.descriptionZh : it.description) || undefined} placeholder={isFallback(it, lang) ? describe(it, lang) : undefined}
          onChange={(e) => patch(it.key, withDescription(it, lang, e.target.value))} />
      </div>
    ),
    color: (it) => (
      <div>
        <FieldLabel htmlFor={`${it.key}-color`}>{t("common.color")}</FieldLabel>
        <Input id={`${it.key}-color`} value={it.color} onChange={(e) => patch(it.key, { color: e.target.value })} />
      </div>
    ),
    size: (it) => (
      <div>
        <FieldLabel htmlFor={`${it.key}-size`}>{t("common.size")}</FieldLabel>
        <Input id={`${it.key}-size`} value={it.size} onChange={(e) => patch(it.key, { size: e.target.value })} />
      </div>
    ),
    requestQuantity: (it) => (
      <div>
        <FieldLabel htmlFor={`${it.key}-requestQuantity`}>{t("samples.requestQuantity")}</FieldLabel>
        <Input inputMode="numeric" className="font-mono tabular" id={`${it.key}-requestQuantity`} value={it.requestQuantity}
          onChange={(e) => patch(it.key, { requestQuantity: e.target.value })} />
      </div>
    ),
    retainedQuantity: (it) => (
      <div>
        <FieldLabel htmlFor={`${it.key}-retainedQuantity`}>{t("samples.retainedQuantity")}</FieldLabel>
        <Input inputMode="numeric" className="font-mono tabular" id={`${it.key}-retainedQuantity`} value={it.retainedQuantity}
          onChange={(e) => patch(it.key, { retainedQuantity: e.target.value })} />
      </div>
    ),
    // Mẫu đối và ghi chú mẫu đối chung một ô: ảnh + nút, rồi ô ghi chú ngay dưới.
    controlSample: (it, i) => (
      <div>
        <FieldLabel>{t("samples.controlSample")}</FieldLabel>
        <div className="flex items-center gap-3">
          <span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-sm border border-line bg-surface">
            {it.controlSampleUrl
              ? <img src={it.controlSampleUrl} alt={t("samples.controlSample")} className="size-full object-cover" />
              : <ImageIcon className="size-5 text-ink-faint" />}
          </span>
          <div className="flex flex-wrap gap-2">
            <SampleFileButton accept={IMAGE_ACCEPT} disabled={readOnly || busy !== null}
              label={busy === `${it.key}:control` ? t("common.uploading") : `${t("samples.controlSample")} ${i+1} · ${t("products.uploadImage")}`}
              onChange={e=>void onLineImage(e,it.key,"control")}/>
            {it.imageUrl ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => patch(it.key, { controlSampleUrl: it.imageUrl, controlSampleKey: it.imageKey })}>
                {t("samples.useProductImage")}
              </Button>
            ) : null}
            {it.controlSampleUrl ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => patch(it.key, { controlSampleUrl: "", controlSampleKey: "" })}>
                {t("products.removeImage")}
              </Button>
            ) : null}
          </div>
        </div>
        <label htmlFor={`${it.key}-controlSampleDescription`} className="mt-2 block text-xs text-ink-soft">{t("samples.controlSampleNotes")}</label>
        <Input id={`${it.key}-controlSampleDescription`} className="mt-1" value={it.controlSampleDescription}
          onChange={(e) => patch(it.key, { controlSampleDescription: e.target.value })} />
      </div>
    ),
    /*
      Ô nhiều dòng, không phải ô một dòng: khách nói sẽ nhập nhiều. Ô một dòng vẫn gõ được dài nhưng chỉ nhìn thấy vài chữ cuối,
      nên người nhập không đọc lại được thứ mình vừa viết. Chiếm cả hàng để hai dòng chữ đủ rộng mà đọc.
    */
    remarks: (it) => (
      <div>
        <FieldLabel htmlFor={`${it.key}-remarks`}>{t("common.remarks")}</FieldLabel>
        <textarea id={`${it.key}-remarks`} rows={2} value={it.remarks} onChange={(e) => patch(it.key, { remarks: e.target.value })}
          className="w-full rounded-sm border border-line-strong bg-surface px-2.5 py-2 text-sm text-ink outline-none focus:border-brand" />
      </div>
    ),
  };

  return (
    <FormGroup
      id="sample-group-lines"
      title={t("sampleWorkspace.groups.lines")}
      // Lời dặn số lượng ghi MỘT lần cho cả nhóm — trước 19/09/2026 nó lặp lại trên từng dòng.
      hint={t("sampleWorkspace.quantityHint")}
      actions={
        <>
          <Button type="button" variant="secondary" size="sm" disabled={readOnly || lines.length >= 50} onClick={onAddProduct}>
            <Plus className="size-3.5" /> {t("samples.addProduct")}
          </Button>
          <Button type="button" variant="ghost" size="sm"
            disabled={readOnly || lines.length >= 50} onClick={() => onChange(previous => [...previous, blankSampleLine(true)])}>
            <Plus className="size-3.5" /> {t("samples.manualEntry")}
          </Button>
        </>
      }
    >
      {lines.length === 0 ? (
        <div className="rounded-sm border border-dashed border-line px-4 py-6 text-center">
          <p className="text-sm text-ink-soft">{t("samples.noItems")}</p>
          <p className="mt-1 text-xs text-ink-faint">{t("samples.noItemsHint")}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {lines.map((it, i) => (
            <div key={it.key} data-sample-line="" className="rounded-sm border border-line bg-sunken/30 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="font-mono tabular text-xs text-ink-faint">{String(i + 1).padStart(2, "0")}</span>
                  {it.isManualEntry ? <Badge tone="brass">{t("samples.manualBadge")}</Badge> : null}
                  {/* Màn hẹp: chỉ phần mô tả được rút gọn — mã là thứ để nhận ra dòng, cắt thành "9001…" thì vô dụng. */}
                  <Button type="button" variant="ghost" size="sm" className="max-w-full" aria-expanded={!collapsed.has(it.key)}
                    onClick={()=>setCollapsed(previous=>{const next=new Set(previous);if(next.has(it.key))next.delete(it.key);else next.add(it.key);return next;})}>
                    <span className="shrink-0 font-mono">{[it.productNo || t("samples.manualEntry"), it.subRef].filter(Boolean).join(" / ")}</span>
                    <span className="min-w-0 truncate text-ink-soft">{describe(it, lang) ? ` · ${describe(it, lang)}` : ""}</span>
                    <span className="shrink-0 text-ink-faint"> · {t(collapsed.has(it.key) ? "sampleWorkspace.expand" : "sampleWorkspace.collapse")}</span>
                  </Button>
                </span>
                <span className="flex items-center gap-1">
                  {/* In tem cho CHÍNH dòng này. Hệ thống cũ chỉ in được dòng hàng ĐẦU TIÊN của đơn và không có cách nào in dòng còn
                      lại (#71) — một đơn mẫu 6 dòng thì 5 dòng không có tem. */}
                  <LabelPrintButton
                    compact
                    productRef={it.productNo || t("samples.manualEntry")}
                    subRef={it.subRef}
                    description={it.description}
                    descriptionZh={it.descriptionZh ?? null}
                    color={it.color}
                    size={it.size}
                    barcode={it.productNo || null}
                    quantity={Number(it.requestQuantity) || null}
                  />
                  <Button type="button" variant="ghost" size="icon-sm" aria-label={t("common.removeLine")}
                    disabled={readOnly || busy !== null} onClick={() => remove(it.key)}>
                    <Trash2 className="size-4 text-ink-faint" />
                  </Button>
                </span>
              </div>
              {!collapsed.has(it.key) && (
                <fieldset disabled={readOnly || busy !== null} className="@container mt-2 min-w-0">
                  <div className="flex flex-col gap-4 @md:flex-row @md:items-start">
                    <div className="shrink-0">{cells.image(it, i)}</div>
                    <div className="@container min-w-0 flex-1 space-y-4">
                      {SAMPLE_LINE_ROWS.map((row, r) => (
                        <FieldGrid key={r}>
                          {row.map((c) => <div key={c.key} className={cn("min-w-0", SPAN_CLASS[c.span])}>{cells[c.key](it, i)}</div>)}
                        </FieldGrid>
                      ))}
                    </div>
                  </div>
                </fieldset>
              )}
            </div>
          ))}
        </div>
      )}

      {err && (
        <p className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger" role="alert">{err}</p>
      )}
    </FormGroup>
  );
}
